import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { SparklesIcon } from "lucide-react";
import { toast } from "sonner";

import { EnlaceVolver } from "@/components/EnlaceVolver";
import { SelectorDeFecha } from "@/components/SelectorDeFecha";
import {
  aNumero,
  conceptoDeTasacion,
  CuadroDeHonorarios,
  lineasDelCuadro,
  totalDelCuadro,
  type CuadroEditable,
} from "@/components/informes/CuadroDeHonorarios";
import {
  documentosDelInforme,
  SelectorDeItems,
} from "@/components/informes/SelectorDeItems";
import { formatearDecimal } from "@/components/informes/formato";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import { Switch } from "@/components/ui/switch";
import { useCrearCotizacion, useInforme } from "@/hooks/useInformes";
import { getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import {
  urlDeLaImagenDelInforme,
  type CotizacionLinea,
  type Informe,
  type ModoGeneracionCotizacion,
} from "@/lib/informes";

/**
 * Cotización del informe (Módulo F, pantalla `18 UN`).
 *
 * Dos modos, y el de IA se MUESTRA aunque esté bloqueado: el flujo con
 * asistencia es parte del diseño del producto y esconderlo lo borraría del
 * mapa mental de quien usa la pantalla. El motor de IA está diferido hasta
 * definir qué se construye (CLAUDE.md raíz), así que "Ejecutar análisis IA"
 * queda deshabilitado con el motivo a la vista — y el servidor lo rechaza
 * también, para que el bloqueo no dependa de un `disabled`.
 */
export function CotizarInforme() {
  const { id } = useParams();
  const informeId = Number(id);
  const ficha = useInforme(informeId);

  if (ficha.isPending) {
    return (
      <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full rounded-xl" />
      </div>
    );
  }

  if (ficha.isError || !ficha.data) {
    return (
      <div className="space-y-4 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
        <EnlaceVolver to="/informes">Volver a informes en proceso</EnlaceVolver>
        <p className="text-destructive text-sm">
          No pudimos cargar este informe. Puede que ya no exista o que la conexión haya fallado.
        </p>
      </div>
    );
  }

  return <Contenido informe={ficha.data} />;
}

/**
 * Con qué montos arranca el cuadro.
 *
 * Al RECOTIZAR se precargan los de la versión anterior: corregir un plazo no
 * puede obligar a volver a tipear el honorario. Si la cotización es ANTERIOR
 * a este cambio y todavía trae varias líneas `tasacion` —una por cada
 * `informe_propiedades` de entonces—, se SUMAN en una sola: el árbol plano ya
 * no distingue esas filas, y perder el monto sería peor que consolidarlo.
 *
 * Los montos pasan por `formatearDecimal` porque el backend los manda como
 * `"34.00"` por el cast `decimal:2`, y precargar eso hace que el ejecutivo lea
 * "34.00" donde había escrito "34".
 */
function cuadroInicial(lineas: CotizacionLinea[] | undefined): CuadroEditable {
  const vacio: CuadroEditable = { tasacion: "", gastos: "", descuento: "", descuentoModo: "uf" };
  if (!lineas || lineas.length === 0) return vacio;

  const sumaTasaciones = lineas
    .filter((linea) => linea.tipo === "tasacion")
    .reduce((total, linea) => total + Number(linea.monto_uf), 0);

  // Un descuento con porcentaje se restaura EN porcentaje: lo que se pactó fue
  // ese número, y el monto en UF es su consecuencia.
  const descuento = lineas.find((l) => l.tipo === "descuento");
  const enPorcentaje = descuento?.porcentaje != null;

  return {
    tasacion: sumaTasaciones > 0 ? formatearDecimal(String(sumaTasaciones)) ?? "" : "",
    gastos: formatearDecimal(lineas.find((l) => l.tipo === "gastos")?.monto_uf ?? null) ?? "",
    descuento:
      formatearDecimal((enPorcentaje ? descuento?.porcentaje : descuento?.monto_uf) ?? null) ?? "",
    descuentoModo: enPorcentaje ? "porcentaje" : "uf",
  };
}

/**
 * El cuerpo, separado del componente que resuelve la carga: el hook de
 * mutación necesita el id del informe ya resuelto, y llamarlo antes de los
 * `return` tempranos significaría invocarlo con datos indefinidos.
 */
function Contenido({ informe }: { informe: Informe }) {
  const navigate = useNavigate();
  const cotizar = useCrearCotizacion(informe.id);

  const [modo, setModo] = useState<ModoGeneracionCotizacion>("manual");
  const [plazo, setPlazo] = useState(
    informe.cotizacion?.plazo_dias_habiles?.toString() ?? "",
  );

  // EL PRECIO YA NO SE TIPEA: se DERIVA del cuadro. Tenerlo como campo aparte
  // era un segundo lugar donde vivía la misma cifra, y el día que no
  // coincidieran ganaría el cuadro, que es lo que se imprime.
  //
  // UNA SOLA LÍNEA DE TASACIÓN: el informe entero es una tasación desde que
  // volvió al árbol plano. Lo único que se completa es su honorario, más un
  // gasto y un descuento.
  const [cuadro, setCuadro] = useState<CuadroEditable>(() =>
    cuadroInicial(informe.cotizacion?.lineas),
  );

  // LA METODOLOGÍA NO SE ELIGE ACÁ (01-09-2026): el documento imprime siempre
  // la estándar, que arma el servidor. Por eso esta pantalla ya no carga el
  // catálogo ni manda `items` — mandar un arreglo vacío sería indistinguible
  // de una elección, y elegir nada deja el apartado en blanco en un papel que
  // el cliente firma. Ver `CotizacionItem::METODOLOGIA_ESTANDAR`.

  // La DOCUMENTACIÓN sale del informe y nace TODA MARCADA: el caso normal es
  // pedirle al cliente lo mismo que el expediente necesita, así que desmarcar
  // algo tiene que ser una decisión y no el trabajo de arranque.
  //
  // Al recotizar manda lo que decía la versión anterior, que puede ser menos.
  const documentos = documentosDelInforme(informe);
  const [documentosElegidos, setDocumentosElegidos] = useState<number[]>(
    informe.cotizacion?.documentos ?? documentos.map((documento) => documento.id),
  );

  // ARRANCA ENCENDIDO, y el `??` es lo que lo decide en los dos casos que
  // importan: sin cotización previa (informe nuevo) y con una anterior a la
  // columna, donde el dato es `null`. Pedir abono es lo habitual en Consultek,
  // así que el interruptor refleja el caso corriente y quien no lo necesita lo
  // apaga. Al recotizar, en cambio, se respeta lo que decía la versión previa.
  const [requiereAbono, setRequiereAbono] = useState(
    informe.cotizacion?.requiere_abono ?? true,
  );
  // Por `formatearDecimal` y no crudo: el backend lo manda como `"40.00"` por
  // el cast `decimal:2`, y precargar eso en el campo hace que el ejecutivo lea
  // "40.00" donde escribió "40".
  const [porcentajeAbono, setPorcentajeAbono] = useState(
    formatearDecimal(informe.cotizacion?.porcentaje_abono ?? null) ?? "",
  );

  // Las dos fechas internas viajan en ISO (`2026-09-03`), que es el formato de
  // `SelectorDeFecha` y el que espera el API. `""` es "sin agendar".
  const [fechaVisita, setFechaVisita] = useState(
    informe.cotizacion?.fecha_visita_estimada ?? "",
  );
  const [fechaEntregaEstimada, setFechaEntregaEstimada] = useState(
    informe.cotizacion?.fecha_entrega_estimada ?? "",
  );

  // "Hay algo tecleado que todavía no es una fecha", que NO es lo mismo que
  // "no hay fecha": el selector emite `""` en los dos casos. Sin este par, un
  // campo con "31-02-2026" adentro se enviaría como "sin agendar" y el envío
  // saldría en verde con el campo pintado de rojo.
  const [visitaIncompleta, setVisitaIncompleta] = useState(false);
  const [entregaEstimadaIncompleta, setEntregaEstimadaIncompleta] = useState(false);

  const [errores, setErrores] = useState<Record<string, string>>({});

  function enviar() {
    setErrores({});

    const precioNumero = totalDelCuadro(cuadro);
    const plazoNumero = Number(plazo);
    const porcentajeNumero = Number(porcentajeAbono.replace(",", "."));
    const faltantes: Record<string, string> = {};

    // Se valida acá además del servidor para no gastar un viaje en lo obvio.
    // La validación REAL es la del backend: este chequeo previo no la
    // reemplaza, y por eso los errores del 422 también se pintan.
    const montoTasacion = aNumero(cuadro.tasacion);
    if (!cuadro.tasacion.trim() || montoTasacion <= 0) {
      faltantes.lineas = "El honorario tiene que ser mayor que cero.";
    } else if (
      cuadro.descuentoModo === "porcentaje" &&
      aNumero(cuadro.descuento) > 100
    ) {
      faltantes.lineas = "El porcentaje del descuento no puede superar el 100 %.";
    } else if (precioNumero <= 0) {
      // Un descuento más grande que el honorario. El cuadro está completo,
      // pero el presupuesto diría que el trabajo cuesta cero o menos.
      faltantes.lineas = "El total tiene que ser mayor que cero: revisa los descuentos.";
    }
    if (!plazo.trim() || !Number.isInteger(plazoNumero) || plazoNumero < 1) {
      faltantes.plazo_dias_habiles = "Ingresa el plazo de entrega en días hábiles.";
    }
    // Solo cuando el interruptor está encendido: con el abono apagado el campo
    // ni siquiera se dibuja, y exigirle un valor bloquearía el formulario en un
    // campo invisible.
    if (requiereAbono) {
      if (
        !porcentajeAbono.trim() ||
        !Number.isFinite(porcentajeNumero) ||
        porcentajeNumero <= 0 ||
        porcentajeNumero > 100
      ) {
        faltantes.porcentaje_abono = "Ingresa un porcentaje de abono entre 1 y 100.";
      }
    }
    // Una fecha a medio escribir NO es una fecha en blanco. Dejarla pasar
    // guardaría "sin agendar" sobre un campo que el usuario cree completo.
    if (visitaIncompleta) {
      faltantes.fecha_visita_estimada = "Completa la fecha con el formato dd-mm-aaaa.";
    }
    if (entregaEstimadaIncompleta) {
      faltantes.fecha_entrega_estimada = "Completa la fecha con el formato dd-mm-aaaa.";
    }
    // Entregar antes de visitar es un error de tipeo: la visita a terreno
    // produce lo que se entrega. Las dos fechas viajan en ISO, así que
    // compararlas como texto ya da el orden cronológico.
    if (fechaVisita && fechaEntregaEstimada && fechaEntregaEstimada < fechaVisita) {
      faltantes.fecha_entrega_estimada =
        "La entrega estimada no puede ser anterior a la visita estimada.";
    }

    if (Object.keys(faltantes).length > 0) {
      setErrores(faltantes);
      return;
    }

    cotizar.mutate(
      {
        // El total del cuadro, no un campo aparte: hay una sola cifra y sale
        // de las líneas que se imprimen.
        precio_uf: precioNumero,
        // Las líneas se ARMAN del cuadro: una por propiedad con su nombre como
        // concepto, más el gasto y el descuento si tienen monto.
        lineas: lineasDelCuadro(cuadro, conceptoDeTasacion(informe)),
        // `items` NO viaja: la metodología dejó de elegirse y el servidor la
        // deriva. Mandar `[]` desde acá la borraría del documento.
        documentos: documentosElegidos,
        plazo_dias_habiles: plazoNumero,
        modo_generacion: "manual",
        requiere_abono: requiereAbono,
        // `null` y no el número tipeado cuando el abono está apagado: si
        // alguien escribió un 40, lo apagó y envía, guardar ese 40 dejaría una
        // fila que dice "no requiere" con un porcentaje adentro.
        porcentaje_abono: requiereAbono ? porcentajeNumero : null,
        // `""` es "sin agendar" en el selector y `null` en el API: son el
        // mismo hecho, expresado en el vocabulario de cada lado.
        fecha_visita_estimada: fechaVisita || null,
        fecha_entrega_estimada: fechaEntregaEstimada || null,
      },
      {
        // NAVEGA a la confirmación en vez de conmutar esta pantalla: como
        // ruta propia, se puede volver a ella desde el documento y sobrevive a
        // un refresh. La mutación ya invalidó la ficha, así que esa pantalla
        // lee la cotización recién creada.
        onSuccess: () => navigate(`/informes/${informe.id}/cotizacion/enviada`),
        onError: (error) => {
          const delServidor = parseValidationErrors(error);
          if (delServidor) {
            setErrores(delServidor);
            toast.error("Revisa los campos marcados.");
            return;
          }
          toast.error(
            getApiErrorMessage(error, "No pudimos generar la cotización. Inténtalo de nuevo."),
          );
        },
      },
    );
  }

  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <EnlaceVolver to={`/informes/${informe.id}`}>Volver al informe</EnlaceVolver>

      <div>
        <p className="text-ink-muted text-caption">
          {informe.cliente_nombre ?? "—"} · {informe.propiedad_nombre ?? "—"}
        </p>
        {informe.cotizacion ? (
          <p className="text-caption text-ink-hint mt-1">
            Ya existe una cotización enviada a revisión (versión{" "}
            <span className="font-mono">{informe.cotizacion.version}</span>). Generar otra la
            reemplaza y anula la anterior.
          </p>
        ) : null}
      </div>

      {/* Los dos modos. `role="group"` y `aria-pressed` porque son botones que
          conmutan una vista, no navegación. */}
      <div className="flex flex-wrap gap-3" role="group" aria-label="Modo de valorización">
        <Button
          type="button"
          variant={modo === "manual" ? "soft" : "ghost"}
          aria-pressed={modo === "manual"}
          onClick={() => setModo("manual")}
        >
          Ingresar valores manualmente
        </Button>
        <Button
          type="button"
          variant={modo === "ia" ? "default" : "secondary"}
          aria-pressed={modo === "ia"}
          onClick={() => setModo("ia")}
        >
          <SparklesIcon className="size-4" />
          Analizar con IA
        </Button>
      </div>

      {/* LA IMAGEN NO SE EDITA ACÁ: se carga en los antecedentes generales del
          informe, desde el alta. Se muestra igual, y de solo lectura, porque es
          lo que va a llevar el documento que el cliente recibe — quien cotiza
          tiene que poder verla antes de mandarla a visar. Editarla en dos
          pantallas dejaría dos lugares escribiendo el mismo dato y ninguno
          sería la fuente.

          UNA SOLA IMAGEN, ya no una por bloque: el nivel que agrupaba varias
          capturas nació del caso incorrecto de la 22160. */}
      {informe.imagen ? (
        <div className="bg-card space-y-4 rounded-xl border p-6">
          <div>
            <p className="text-label text-ink uppercase">Imagen satelital de la propiedad</p>
            <p className="text-ink-hint text-xs">
              Se edita en los antecedentes generales del informe.
            </p>
          </div>
          <img
            src={urlDeLaImagenDelInforme(informe.id, informe.imagen.hash)}
            alt="Imagen satelital de la propiedad"
            className="max-h-72 w-full rounded-md object-contain"
          />
          {informe.nota_imagen ? (
            <p className="text-ink-hint text-xs">{informe.nota_imagen}</p>
          ) : null}
        </div>
      ) : null}

      <div className="bg-card space-y-5 rounded-xl border p-6">
        {modo === "ia" ? (
          <div className="space-y-2">
            <p className="text-ink-muted max-w-[70ch] text-sm">
              La IA revisa la agenda y capacidad de carga de los usuarios asignados, el tipo y uso
              de la tasación, y las características descritas de la propiedad para sugerir un
              precio y un plazo de entrega. Los valores quedan siempre editables antes de enviar.
            </p>
            {/* El motivo del bloqueo se dice, no se esconde en un tooltip: un
                botón apagado sin explicación se lee como un error. */}
            <p className="text-warning-soft-foreground text-sm">
              El análisis con IA todavía no está disponible. Puedes cargar el precio y el plazo a
              mano y enviar la cotización igual.
            </p>
          </div>
        ) : (
          <p className="text-ink-muted max-w-[70ch] text-sm">
            Completa el honorario de cada propiedad y el plazo de entrega. Al enviar, el informe
            queda a la espera del visado del revisor.
          </p>
        )}

        {/* El cuadro va PRIMERO y a lo ancho: es lo que define el precio y lo
            que más se edita. El plazo y el abono son condiciones sobre esa
            cifra, así que van debajo. */}
        <CuadroDeHonorarios
          cuadro={cuadro}
          onChange={setCuadro}
          error={errores.lineas ?? errores.precio_uf}
        />

        {/* La DOCUMENTACIÓN que se le pide al cliente. Va después del cuadro
            porque describe el trabajo que se está cobrando: primero cuánto,
            después qué hace falta para hacerlo.

            La METODOLOGÍA iba en este mismo bloque y se retiró: hoy es fija y
            la arma el servidor. */}
        <SelectorDeItems
          documentos={documentos}
          documentosElegidos={documentosElegidos}
          onDocumentosChange={setDocumentosElegidos}
        />

        <div className="grid gap-5 md:grid-cols-2">
          <Campo
            id="plazo-entrega"
            etiqueta="Plazo de entrega (días hábiles)"
            error={errores.plazo_dias_habiles}
          >
            <Input
              id="plazo-entrega"
              inputMode="numeric"
              placeholder="Ej: 15"
              value={plazo}
              onChange={(evento) => setPlazo(evento.target.value)}
              aria-invalid={Boolean(errores.plazo_dias_habiles)}
            />
            {/* El plazo no arranca al cotizar: se dice acá para que el
                ejecutivo sepa qué está comprometiendo. */}
            <p className="text-ink-hint text-xs">
              Es un plazo REFERENCIAL: el documento dice "a contar de la visita de
              inspección", y esa visita todavía no está agendada, así que no hay
              fecha que calcular.
            </p>
          </Campo>
        </div>

        {/* EL ABONO ES UNA CONDICIÓN COMERCIAL, así que queda junto al precio y
            sobre la línea que separa lo que ve el cliente de lo que no. */}
        <div className="space-y-4 border-t pt-5">
          <div className="flex items-center gap-3">
            <Switch
              id="requiere-abono"
              checked={requiereAbono}
              onCheckedChange={setRequiereAbono}
            />
            {/* El rótulo va con `htmlFor`, no envolviendo al Switch: Radix ya
                expone el control como checkbox y anidarlo duplicaría el
                objetivo de clic. */}
            <Label htmlFor="requiere-abono">Requiere abono</Label>
          </div>

          {/* El campo aparece SOLO con el interruptor encendido. Apagado, un
              porcentaje deshabilitado a la vista invita a completar algo que no
              se va a guardar. */}
          {requiereAbono ? (
            <div className="grid gap-5 md:grid-cols-2">
              <Campo
                id="porcentaje-abono"
                etiqueta="Porcentaje de abono (%)"
                error={errores.porcentaje_abono}
              >
                <Input
                  id="porcentaje-abono"
                  inputMode="decimal"
                  placeholder="Ej: 40"
                  value={porcentajeAbono}
                  onChange={(evento) => setPorcentajeAbono(evento.target.value)}
                  aria-invalid={Boolean(errores.porcentaje_abono)}
                />
                <p className="text-ink-hint text-xs">
                  Se calcula sobre el valor del servicio y se imprime en la cotización que
                  recibe el cliente.
                </p>
              </Campo>
            </div>
          ) : null}
        </div>

        {/* PLANIFICACIÓN INTERNA. Va en su propio bloque y con el aviso a la
            vista porque la diferencia importa: todo lo de arriba se le ofrece
            al cliente y esto no. Sin el rótulo, quien completa el formulario no
            tiene cómo saber cuál de las dos fechas de entrega está tocando. */}
        <div className="space-y-4 border-t pt-5">
          <div className="space-y-1">
            <p className="text-label text-ink uppercase">Planificación interna</p>
            <p className="text-ink-hint max-w-[70ch] text-xs">
              No se imprimen en la cotización ni se le envían al cliente: son la agenda del
              equipo. Lo que se pacta sigue siendo el plazo en días hábiles de arriba, que
              corre a contar de la visita de inspección. Puedes escribirlas directamente o
              elegirlas del calendario, y dejarlas en blanco si todavía no hay fecha.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-2">
            <Campo
              id="fecha-visita-estimada"
              etiqueta="Fecha de visita estimada"
              error={errores.fecha_visita_estimada}
            >
              <SelectorDeFecha
                id="fecha-visita-estimada"
                value={fechaVisita}
                onChange={setFechaVisita}
                onTextoIncompleto={setVisitaIncompleta}
                invalido={Boolean(errores.fecha_visita_estimada)}
              />
            </Campo>

            <Campo
              id="fecha-entrega-estimada"
              etiqueta="Fecha de entrega estimada"
              error={errores.fecha_entrega_estimada}
            >
              <SelectorDeFecha
                id="fecha-entrega-estimada"
                value={fechaEntregaEstimada}
                onChange={setFechaEntregaEstimada}
                onTextoIncompleto={setEntregaEstimadaIncompleta}
                invalido={Boolean(errores.fecha_entrega_estimada)}
              />
            </Campo>
          </div>
        </div>

        {/* El error de estado del 422 no cuelga de ningún campo: se muestra
            solo, porque habla del informe y no de lo que se tipeó. */}
        {errores.estado ? <p className="text-destructive text-sm">{errores.estado}</p> : null}

        <div className="flex flex-wrap items-center gap-3">
          <Button
            type="button"
            variant="outline"
            disabled
            title="El análisis con IA todavía no está disponible."
          >
            Ejecutar análisis IA
          </Button>

          <Button type="button" onClick={enviar} disabled={cotizar.isPending}>
            {cotizar.isPending ? <Spinner /> : null}
            Enviar a revisión
          </Button>

          <Button type="button" variant="ghost" onClick={() => navigate(`/informes/${informe.id}`)}>
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Campo con rótulo y mensaje de error, para no repetir el trío en cada uno. */
function Campo({
  id,
  etiqueta,
  error,
  children,
}: {
  id: string;
  etiqueta: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{etiqueta}</Label>
      {children}
      {error ? <p className="text-destructive text-xs font-medium">{error}</p> : null}
    </div>
  );
}
